import { Alert, Button, Spin } from "antd";
import {
  Aviso,
  ResumenAvisos,
  textoDelPlazoParaSubsanar,
} from "../../../utils/avisosPostulacion";
import { PASOS } from "./pasosPostulacion";

interface IProps {
  resumen: ResumenAvisos;
  cargando: boolean;
  /** Hay una lectura en curso: todavía no se sabe en qué quedó la propuesta. */
  recargando?: boolean;
  /** Cuándo sale el primer informe, si el cronograma lo tiene */
  fechaDelInforme?: any;
  /** Hasta cuándo hay para corregir lo que ese informe pida subsanar */
  fechaLimiteSubsanar?: any;
  /** La clave del paso que se está viendo, para no ofrecer ir a donde ya se está */
  pasoActual?: string;
  irAlPaso: (clave: string) => void;
}

/**
 * Lo que le pasaría hoy a la postulación, mientras todavía se puede arreglar.
 *
 * Esto vive en el wizard, donde se crea y se edita la propuesta, así que lo
 * que advierte es siempre lo del PRIMER informe de verificación: para cuando
 * sale el informe final la convocatoria ya cerró, la propuesta no se edita y
 * la subsanación va por su propio camino.
 *
 * Los textos de cada documento son los oficiales, sin reescribir: lo que se
 * lee aquí es lo que saldría en el informe. No decide nada. Solo mira lo que
 * se puede comprobar sin abrir los archivos, así que quedarse sin avisos no
 * es un visto bueno, y eso se dice.
 */

const TITULARES: { [estado: string]: { titulo: string; explicacion: string } } = {
  subsanar: {
    titulo: "Esto te lo van a pedir subsanar",
    explicacion:
      "Si tu propuesta queda así, en el primer informe de verificación te pedirán corregir lo siguiente. Tendrás un plazo para hacerlo, pero es más fácil resolverlo ahora.",
  },
  rechazar: {
    titulo: "Esto deja tu propuesta fuera",
    explicacion:
      "Los documentos técnicos no son subsanables: si faltan, la propuesta se rechaza y no hay plazo para corregirla.",
  },
};

const tituloDelPaso = (clave: string): string =>
  PASOS.find((paso) => paso.clave === clave)?.titulo ?? "el paso que falta";

const ListaDeAvisos = ({
  avisos,
  estado,
  fechaDelInforme,
  fechaLimiteSubsanar,
  pasoActual,
  irAlPaso,
}: {
  avisos: Aviso[];
  estado: "subsanar" | "rechazar";
  fechaDelInforme?: any;
  fechaLimiteSubsanar?: any;
  pasoActual?: string;
  irAlPaso: (clave: string) => void;
}) => {
  if (avisos.length === 0) return null;

  const { titulo, explicacion } = TITULARES[estado];

  /*
    La frase del plazo es la misma que va en el radicado y en las
    observaciones de la propuesta, así que sale de un solo sitio: si los tres
    no coincidieran, la persona leería tres plazos para lo mismo. Lo de
    rechazo no lleva plazo, porque no se subsana.
  */
  const plazo = textoDelPlazoParaSubsanar(
    fechaDelInforme,
    fechaLimiteSubsanar,
    estado === "subsanar"
  );

  // Todos los avisos de hoy se arreglan en el mismo paso, pero se toma del
  // aviso y no de una constante, para que siga sirviendo si eso cambia.
  const paso = avisos[0].paso;

  return (
    <Alert
      className="mb-3"
      type={estado === "rechazar" ? "error" : "warning"}
      showIcon
      message={
        <span className="font-family-Montserrat-SemiBold font-size-14px">
          {titulo} ({avisos.length})
        </span>
      }
      description={
        <div className="font-family-Montserrat-Regular font-size-12px">
          <p className="mb-2">
            {explicacion} {plazo}
          </p>
          <ul className="mb-2 ps-3">
            {avisos.map((aviso, indice) => (
              <li key={`${aviso.grupo}-${aviso.documento}-${indice}`} className="mb-1">
                <span className="font-family-Montserrat-SemiBold">
                  {aviso.documento || "Documento sin nombre"}:
                </span>{" "}
                {aviso.texto}
              </li>
            ))}
          </ul>
          {paso !== pasoActual && (
            <Button
              type="link"
              className="p-0 font-size-12px"
              onClick={() => irAlPaso(paso)}
            >
              Ir a {tituloDelPaso(paso)}
            </Button>
          )}
        </div>
      }
    />
  );
};

export const PanelAlertas = ({
  resumen,
  cargando,
  recargando,
  fechaDelInforme,
  fechaLimiteSubsanar,
  pasoActual,
  irAlPaso,
}: IProps) => {
  // Mientras no se sepa qué hay guardado no se afirma nada, en ningún sentido.
  if (cargando) return null;

  /*
    Con una lectura en curso tampoco. Al cambiar de perfil los documentos
    guardados son todavía los del anterior, y filtrarlos por el nuevo daba
    cero avisos: salía el cartel verde de "no vemos nada que falte" y un
    instante después las alertas de verdad lo desmentían.
  */
  if (recargando) {
    return (
      <div className="container-fluid2 mt-4">
        <div
          className="d-flex align-items-center font-family-Montserrat-Regular font-size-12px font-color-707070"
          style={{ gap: 8 }}
          role="status"
          aria-live="polite"
        >
          <Spin size="small" />
          Revisando tu propuesta…
        </div>
      </div>
    );
  }

  const porRechazar = resumen.avisos.filter((a) => a.estado === "rechazar");
  const porSubsanar = resumen.avisos.filter((a) => a.estado === "subsanar");

  const salvedad =
    "Esta revisión es automática y alcanza solo lo que se puede comprobar sin abrir los archivos: lo que no aportaste y los formatos que se diligencian en línea. Un documento ya cargado no se revisa por dentro aquí, y la decisión la toma el profesional que verifica.";

  return (
    <div className="container-fluid2 mt-4">
      {/* El rechazo primero: es la consecuencia que no tiene vuelta atrás. */}
      <ListaDeAvisos
        avisos={porRechazar}
        estado="rechazar"
        fechaDelInforme={fechaDelInforme}
        pasoActual={pasoActual}
        irAlPaso={irAlPaso}
      />
      <ListaDeAvisos
        avisos={porSubsanar}
        estado="subsanar"
        fechaDelInforme={fechaDelInforme}
        pasoActual={pasoActual}
        irAlPaso={irAlPaso}
      />

      {resumen.avisos.length === 0 ? (
        <Alert
          type="success"
          showIcon
          message={
            <span className="font-family-Montserrat-SemiBold font-size-14px">
              Por ahora no vemos nada que falte
            </span>
          }
          description={
            <span className="font-family-Montserrat-Regular font-size-12px">
              {salvedad}
            </span>
          }
        />
      ) : (
        <p className="font-family-Montserrat-Regular font-size-12px font-color-707070">
          {salvedad}
        </p>
      )}
    </div>
  );
};
